/**
 * Subscription Tiers Configuration (web copy)
 *
 * Ported verbatim from the mobile app's src/constans/subscriptionTiers.js
 * (pure JS, no React Native imports). Keep in sync manually for now; extract a
 * shared package later if drift becomes a problem.
 *
 * Tiers:
 * - FREE: Default tier with basic features and limitations
 * - PREMIUM: Unlocks Pro app features (modes, games, etc.)
 * - PREMIUM_PLUS (future): Premium + subscription features (media questions, etc.)
 */

// ============================================
// SUBSCRIPTION TIER DEFINITIONS
// ============================================
export const SUBSCRIPTION_TIERS = {
  FREE: "free",
  PREMIUM: "premium",
  PREMIUM_PLUS: "premium_plus", // Future tier
};

// Default tier for new users
export const DEFAULT_TIER = SUBSCRIPTION_TIERS.FREE;

// ============================================
// TIER LIMITS - Defines limits for each tier
// ============================================
export const TIER_LIMITS = {
  [SUBSCRIPTION_TIERS.FREE]: {
    // Game creation limit
    MAX_GAMES: 3,

    // Custom modes limit (how many custom modes can be added)
    MAX_CUSTOM_MODES: 1,

    // Maximum questions per custom mode
    MAX_QUESTIONS_PER_MODE: 50,

    // Show ads
    SHOW_ADS: true,

    // Can create private modes
    CAN_CREATE_PRIVATE_MODES: false,

    // Can create imposter games
    CAN_CREATE_IMPOSTER_GAMES: false,

    // Max modes selectable per game
    MAX_MODES_PER_GAME: 6,
  },

  [SUBSCRIPTION_TIERS.PREMIUM]: {
    MAX_GAMES: Infinity,
    MAX_CUSTOM_MODES: Infinity,
    MAX_QUESTIONS_PER_MODE: Infinity,
    SHOW_ADS: false,
    CAN_CREATE_PRIVATE_MODES: true,
    CAN_CREATE_IMPOSTER_GAMES: true,
    MAX_MODES_PER_GAME: Infinity,
  },

  [SUBSCRIPTION_TIERS.PREMIUM_PLUS]: {
    // Same as Premium
    MAX_GAMES: Infinity,
    MAX_CUSTOM_MODES: Infinity,
    MAX_QUESTIONS_PER_MODE: Infinity,
    SHOW_ADS: false,
    CAN_CREATE_PRIVATE_MODES: true,
    CAN_CREATE_IMPOSTER_GAMES: true,
    MAX_MODES_PER_GAME: Infinity,

    // Additional Premium+ features (future)
    CAN_ADD_VIDEO_QUESTIONS: true,
    CAN_ADD_AUDIO_QUESTIONS: true,
    CAN_ADD_MULTIPLE_CHOICE: true,
    CAN_VIEW_OTHERS_QUESTIONS: true,
  },
};

// ============================================
// FEATURE ACCESS BY TIER
// ============================================
export const TIER_FEATURES = {
  // Features unlocked by PREMIUM tier
  PREMIUM: {
    UNLIMITED_GAMES: "unlimitedGames",
    UNLIMITED_CUSTOM_MODES: "unlimitedCustomModes",
    UNLIMITED_QUESTIONS: "unlimitedQuestions",
    NO_ADS: "noAds",
    PREMIUM_MODES: "premiumModes", // Locked official modes
    PREMIUM_PAID_GAMES: "premiumPaidGames", // Locked paid games
    ONLINE_PREMIUM_MODES: "onlinePremiumModes", // Locked online modes
    PRIVATE_CUSTOM_MODES: "privateCustomModes",
    IMPOSTER_CREATE_GAME: "imposterCreateGame",
  },

  // Additional features for PREMIUM_PLUS tier (future)
  PREMIUM_PLUS: {
    VIDEO_QUESTIONS: "videoQuestions",
    AUDIO_QUESTIONS: "audioQuestions",
    MULTIPLE_CHOICE: "multipleChoice",
    VIEW_OTHERS_QUESTIONS: "viewOthersQuestions",
  },
};

// All features that require PREMIUM or higher
export const PREMIUM_FEATURES = Object.values(TIER_FEATURES.PREMIUM);

// All features that require PREMIUM_PLUS
export const PREMIUM_PLUS_FEATURES = [
  ...PREMIUM_FEATURES,
  ...Object.values(TIER_FEATURES.PREMIUM_PLUS),
];

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Get the limit value for a specific tier
 * @param {string} tier - The subscription tier
 * @param {string} limitName - Name of the limit
 * @returns {number|boolean} The limit value
 */
export const getTierLimit = (tier, limitName) => {
  const tierLimits = TIER_LIMITS[tier] || TIER_LIMITS[DEFAULT_TIER];
  return tierLimits[limitName];
};

/**
 * Check if a limit has been reached for a tier
 * @param {string} tier - The subscription tier
 * @param {string} limitName - Name of the limit
 * @param {number} currentValue - Current count/value to check against
 * @returns {boolean} True if limit is reached
 */
export const isTierLimitReached = (tier, limitName, currentValue) => {
  const limit = getTierLimit(tier, limitName);
  if (limit === Infinity || limit === true) return false;
  return currentValue >= limit;
};

/**
 * Check if a feature is available for a tier
 * @param {string} tier - The subscription tier
 * @param {string} featureName - Feature name from TIER_FEATURES
 * @returns {boolean} True if feature is available
 */
export const isFeatureAvailableForTier = (tier, featureName) => {
  // Premium+ has all features
  if (tier === SUBSCRIPTION_TIERS.PREMIUM_PLUS) {
    return true;
  }

  // Premium has premium features
  if (tier === SUBSCRIPTION_TIERS.PREMIUM) {
    return PREMIUM_FEATURES.includes(featureName);
  }

  // Free tier has no premium features
  return false;
};

/**
 * Get the minimum tier required for a feature
 * @param {string} featureName - Feature name
 * @returns {string} The minimum required tier
 */
export const getRequiredTierForFeature = (featureName) => {
  if (Object.values(TIER_FEATURES.PREMIUM_PLUS).includes(featureName)) {
    return SUBSCRIPTION_TIERS.PREMIUM_PLUS;
  }
  if (PREMIUM_FEATURES.includes(featureName)) {
    return SUBSCRIPTION_TIERS.PREMIUM;
  }
  return SUBSCRIPTION_TIERS.FREE;
};

/**
 * Check if tier A is higher or equal to tier B
 * @param {string} tierA - First tier
 * @param {string} tierB - Second tier
 * @returns {boolean} True if tierA >= tierB
 */
export const isTierHigherOrEqual = (tierA, tierB) => {
  const tierOrder = {
    [SUBSCRIPTION_TIERS.FREE]: 0,
    [SUBSCRIPTION_TIERS.PREMIUM]: 1,
    [SUBSCRIPTION_TIERS.PREMIUM_PLUS]: 2,
  };

  return (tierOrder[tierA] || 0) >= (tierOrder[tierB] || 0);
};

// ============================================
// UPGRADE PROMPT MESSAGES (Arabic)
// ============================================
export const TIER_UPGRADE_MESSAGES = {
  GAMES_LIMIT: {
    title: "وصلت للحد الأقصى",
    message: `يمكنك إنشاء ${TIER_LIMITS[SUBSCRIPTION_TIERS.FREE].MAX_GAMES} ألعاب فقط`,
    feature: "ألعاب غير محدودة",
    requiredTier: SUBSCRIPTION_TIERS.PREMIUM,
  },
  CUSTOM_MODES_LIMIT: {
    title: "وصلت للحد الأقصى",
    message: `يمكنك إضافة ${TIER_LIMITS[SUBSCRIPTION_TIERS.FREE].MAX_CUSTOM_MODES} فئة مجتمع فقط`,
    feature: "فئات مجتمع بلا حدود",
    requiredTier: SUBSCRIPTION_TIERS.PREMIUM,
  },
  QUESTIONS_LIMIT: {
    title: "وصلت للحد الأقصى",
    message: `يمكنك إضافة ${TIER_LIMITS[SUBSCRIPTION_TIERS.FREE].MAX_QUESTIONS_PER_MODE} سؤال فقط لكل فئة`,
    feature: "أسئلة غير محدودة",
    requiredTier: SUBSCRIPTION_TIERS.PREMIUM,
  },
  PREMIUM_MODE: {
    title: "فئة مميزة",
    message: "هذه الفئة متوفرة فقط للمشتركين",
    feature: "جميع الفئات المميزة",
    requiredTier: SUBSCRIPTION_TIERS.PREMIUM,
  },
  PREMIUM_PAID_GAME: {
    title: "لعبة مميزة",
    message: "هذه اللعبة متوفرة فقط للمشتركين",
    feature: "جميع الألعاب الجاهزة",
    requiredTier: SUBSCRIPTION_TIERS.PREMIUM,
  },
  PRIVATE_MODE: {
    title: "فئة خاصة",
    message: "إنشاء فئات خاصة متاح للمشتركين فقط",
    feature: "فئات خاصة",
    requiredTier: SUBSCRIPTION_TIERS.PREMIUM,
  },
  VIDEO_QUESTIONS: {
    title: "ميزة مميزة",
    message: "إضافة فيديو للأسئلة متاح للمشتركين فقط",
    feature: "أسئلة بالفيديو",
    requiredTier: SUBSCRIPTION_TIERS.PREMIUM_PLUS,
  },
  AUDIO_QUESTIONS: {
    title: "ميزة مميزة",
    message: "إضافة صوت للأسئلة متاح للمشتركين فقط",
    feature: "أسئلة صوتية",
    requiredTier: SUBSCRIPTION_TIERS.PREMIUM_PLUS,
  },
  MULTIPLE_CHOICE: {
    title: "ميزة مميزة",
    message: "الأسئلة متعددة الخيارات متاحة للمشتركين فقط",
    feature: "خيارات متعددة",
    requiredTier: SUBSCRIPTION_TIERS.PREMIUM_PLUS,
  },
  VIEW_QUESTIONS: {
    title: "ميزة مميزة",
    message: "عرض أسئلة الآخرين متاح للمشتركين فقط",
    feature: "عرض أسئلة الآخرين",
    requiredTier: SUBSCRIPTION_TIERS.PREMIUM_PLUS,
  },
  GENERIC: {
    title: "ميزة مميزة",
    message: "هذه الميزة متوفرة فقط للمشتركين",
    feature: "جميع الميزات",
    requiredTier: SUBSCRIPTION_TIERS.PREMIUM,
  },
};

// ============================================
// PREMIUM BENEFITS (for display)
// ============================================
export const PREMIUM_BENEFITS = [
  { icon: "infinity", text: "ألعاب غير محدودة", color: "#4CAF50" },
  { icon: "layers", text: "فئات مجتمع بلا حدود", color: "#2196F3" },
  { icon: "lock-open", text: "جميع الفئات المميزة", color: "#FF9800" },
  { icon: "videocam", text: "أسئلة بالفيديو", color: "#00BCD4" },
  { icon: "mic", text: "أسئلة صوتية", color: "#8BC34A" },
  { icon: "list", text: "خيارات متعددة", color: "#FFC107" },
  { icon: "eye", text: "عرض أسئلة الآخرين", color: "#673AB7" },
];

export const PREMIUM_PLUS_BENEFITS = [
  ...PREMIUM_BENEFITS,
  { icon: "videocam", text: "أسئلة بالفيديو", color: "#00BCD4" },
  { icon: "mic", text: "أسئلة صوتية", color: "#8BC34A" },
  { icon: "list", text: "خيارات متعددة", color: "#FFC107" },
  { icon: "eye", text: "عرض أسئلة الآخرين", color: "#673AB7" },
];
